import { useFocusEffect, useRoute } from '@react-navigation/native';
import { useCallback, useState } from 'react';
import { HeaderConfig } from '@v2/pages/common/components/ContextHeader/ContextHeader';
import { PartSearchItem, ProductionOrderSearchItem } from '@interfaces/search';
import { useSelector } from 'react-redux';
import {
  selectForceUpdateHeaderConfig,
  setNcFileId,
  setNcFiles,
  setParts,
  setPartId,
  setNcFileData,
  setProductionOrders,
  setProductionOrderId,
} from '../../../../../reducers/details';
import { GetBatchesByEntityId, GetNCFilesByEntityId, LoadSingleBatch, SearchEntity } from '@interfaces/methods';
import { parseRouteParamAsNumber } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { setBatchId } from '@reducers/batch';

interface UseProductionOrderDetailsProps {
  productionOrderId?: number;
  productionOrdersSearch: SearchEntity<ProductionOrderSearchItem>;
  getNCFilesByProductionOrderId?: GetNCFilesByEntityId;
  updateHeaderConfig?: (headerConfig: HeaderConfig) => void;
  getProductionBatchByProductionOrderId?: GetBatchesByEntityId;
  getAllNcFiles?: boolean;
  loadSingleBatch?: LoadSingleBatch;
}

export const useProductionOrderDetails = ({
  productionOrderId: _productionOrderId,
  productionOrdersSearch,
  updateHeaderConfig,
  getNCFilesByProductionOrderId,
  getProductionBatchByProductionOrderId,
  getAllNcFiles,
  loadSingleBatch,
}: UseProductionOrderDetailsProps) => {
  const route = useRoute();
  const dispatch = useAppDispatch();
  const forceUpdateHeaderConfig = useSelector(selectForceUpdateHeaderConfig);

  const [ncFileDataLoading, setNcFileDataLoading] = useState<boolean>(true);
  const [partDataLoading, setPartDataLoading] = useState<boolean>(true);
  const [productionOrderDataLoading, setProductionOrderDataLoading] = useState<boolean>(true);

  const productionOrderId = _productionOrderId ?? parseRouteParamAsNumber(route.params?.productionOrderId);

  const loadAndSetProductionOrder = async () => {
    if (productionOrderId == null || productionOrdersSearch == null) {
      setProductionOrderDataLoading(false);
      return;
    }

    const result = await productionOrdersSearch({
      data: {},
      filter: { oDataFilter: `ProductionOrderId eq ${productionOrderId}` },
      select: [],
      ignoreHistory: true,
      resetHistory: true,
    });
    if (result.searchResult) {
      dispatch(setProductionOrders(result.searchResult));
      if (result.searchResult.length === 1) {
        dispatch(setProductionOrderId(result.searchResult?.[0]?.productionOrderId));
      }
    }
    setProductionOrderDataLoading(false);
    return result;
  };

  const loadAndSetNcFileData = async () => {
    if (productionOrderId == null || !getNCFilesByProductionOrderId) {
      setNcFileDataLoading(false);
      return;
    }

    const ncFileData = await getNCFilesByProductionOrderId(productionOrderId, !getAllNcFiles ? { top: 1 } : null);
    if (ncFileData?.searchResult) {
      if (ncFileData?.searchResult?.length === 1) {
        dispatch(setNcFileData(ncFileData?.searchResult?.[0]));
        dispatch(setNcFileId(ncFileData?.searchResult?.[0]?.ncFileId));
      }
      dispatch(setNcFiles(ncFileData.searchResult));
    }
    setNcFileDataLoading(false);
    return ncFileData;
  };

  const setPartData = (productionOrder: ProductionOrderSearchItem | null) => {
    if (!productionOrder) {
      setPartDataLoading(false);
      dispatch(setParts([]));
      return;
    }

    const part: PartSearchItem = {
      createdBy: productionOrder.createdBy,
      createdOn: productionOrder.createdOn,
      customerName: productionOrder.customerName,
      description: productionOrder.partDescription,
      drawing: productionOrder.partDrawingNumber,
      drawingRevisionPosition: productionOrder.partDrawingRevision + productionOrder.partDrawingPosition,
      imageData: productionOrder.imageData,
      imageDocumentId: productionOrder.imageDocumentId,
      imageHash: productionOrder.imageHash,
      linkedNcFiles: productionOrder.linkedNcFiles,
      modifiedBy: productionOrder.modifiedBy,
      modifiedOn: productionOrder.modifiedOn,
      partId: productionOrder.partId,
      partName: productionOrder.partNumber,
      position: productionOrder.partDrawingPosition,
      revision: productionOrder.partDrawingRevision,
      score: productionOrder.score,
    };

    dispatch(setParts([part]));
    dispatch(setPartId(part.partId));

    setPartDataLoading(false);
  };

  const loadBatches = async () => {
    if (!getProductionBatchByProductionOrderId) return;
    const batches = await getProductionBatchByProductionOrderId({
      id: productionOrderId,
      select: ['ProductionBatchId', 'BatchNumber'],
      filters: { top: 1 },
    });

    const loadedBatchId = batches.searchResult?.[0]?.productionBatchId;
    if (batches?.totalResultCount === 1) {
      dispatch(setBatchId(loadedBatchId || null));
      await loadSingleBatch?.(loadedBatchId);
    }
    return batches;
  };

  const loadDataAndDefineHeader = async () => {
    const productionOrder = await loadAndSetProductionOrder();

    setPartData(productionOrder?.totalResultCount === 1 ? productionOrder?.searchResult?.[0] : null);
    const batches = await loadBatches();
    const ncFiles = await loadAndSetNcFileData();

    const ncFileHeader = {
      ncFile: ncFiles?.totalResultCount === 1 ? ncFiles?.searchResult?.[0].ncFileName : `#${ncFiles?.totalResultCount ?? 0}`,
      ncFileId: ncFiles?.totalResultCount === 1 ? ncFiles?.searchResult?.[0]?.ncFileId : '',
      ncFileStatus: ncFiles?.totalResultCount === 1 ? ncFiles?.searchResult?.[0]?.ncFileStatus : '',
    };

    const batchHeader = batches?.totalResultCount === 1 ? batches?.searchResult?.[0]?.batchNumber : `#${batches?.totalResultCount ?? 0}`;

    if (updateHeaderConfig) {
      updateHeaderConfig({
        job: '',
        batch: batchHeader,
        ...ncFileHeader,
        part: {
          name: productionOrder?.searchResult?.[0]?.partNumber ?? '',
          id: productionOrder?.searchResult?.[0]?.partId ?? '',
        },
        productionOrder: productionOrder?.searchResult?.[0]?.productionOrderNumber ?? '',
        tools: '',
      });
    }
  };

  useFocusEffect(
    useCallback(() => {
      let isActive = true;

      const fetchData = async () => {
        if (isActive) {
          await loadDataAndDefineHeader();
        }
      };

      fetchData();

      return () => {
        isActive = false;
      };
    }, [productionOrderId, forceUpdateHeaderConfig])
  );

  return {
    ncFileDataLoading,
    partDataLoading,
    productionOrderDataLoading,
  };
};
